import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../utils/currencyUtils';
import { Link } from 'react-router-dom';
import { Building } from '../types/building';
import { LL97Service } from '../services/ll97Service';

interface TargetPropertiesAnalysisProps {
    buildings: Building[];
    onBack: () => void;
}

interface TargetProperty {
    name: string;
    address: string;
    owner: string;
    ownerContact?: string;
    notes: string;
    building?: Building;
    ll97Analysis?: {
        isCompliant: boolean;
        annualFine: number;
        emissionsGap: number;
        potentialSavings: number;
    };
}

const TargetPropertiesAnalysis: React.FC<TargetPropertiesAnalysisProps> = ({ buildings, onBack }) => {
    const [selectedOwner, setSelectedOwner] = useState<string>('all');
    const [showOnlyNonCompliant, setShowOnlyNonCompliant] = useState(false);

    const targetProperties: TargetProperty[] = [
        // F&T Group
        {
            name: "Tangram",
            address: "133-36 37th Ave, Flushing",
            owner: "F&T Group",
            ownerContact: "Michael Meyer, Chris Xiao",
            notes: "Mixed-use with large retail facing LL97 fines on office portions"
        },
        {
            name: "Flushing Commons Phase I",
            address: "38-18 Union St, Flushing",
            owner: "F&T Group",
            ownerContact: "Michael Meyer, Chris Xiao",
            notes: "Large mixed-use development in downtown Flushing"
        },
        {
            name: "Flushing Commons Phase II",
            address: "39-08 Janet Pl, Flushing",
            owner: "F&T Group",
            ownerContact: "Michael Meyer, Chris Xiao",
            notes: "Continuation of major mixed-use project"
        },
        {
            name: "One Fulton Square",
            address: "39-16 Prince St, Flushing",
            owner: "F&T Group",
            ownerContact: "Michael Meyer, Chris Xiao",
            notes: "Mixed-use development with retail and office space"
        },
        {
            name: "Queens Crossing",
            address: "136-17 39th Ave, Flushing",
            owner: "F&T Group",
            ownerContact: "Michael Meyer, Chris Xiao",
            notes: "Major retail and office complex"
        },
        {
            name: "Prince Center",
            address: "39-15 Prince St, Flushing",
            owner: "F&T Group",
            ownerContact: "Michael Meyer, Chris Xiao",
            notes: "Commercial office building"
        },

        // Century Development Group
        {
            name: "The Farrington Hotel & Condo",
            address: "134-37 35th Ave, Flushing",
            owner: "Century Development Group",
            ownerContact: "George Xu",
            notes: "Hotel and residential complex in LL97 zone"
        },
        {
            name: "98-unit Residential Tower",
            address: "135-01 35th Ave, Flushing",
            owner: "Century Development Group",
            ownerContact: "George Xu",
            notes: "New development filed Jan 2023, in LL97 zone"
        },
        {
            name: "14-story Mixed-Use Tower",
            address: "134-03 35th Ave, Flushing",
            owner: "Century Development Group",
            ownerContact: "George Xu",
            notes: "Commercial and residential tower"
        },
        {
            name: "Leavitt St Condo Project",
            address: "35-32 Leavitt St, Flushing",
            owner: "Century Development Group",
            ownerContact: "George Xu",
            notes: "Residential development in Flushing"
        },

        // United Construction & Development
        {
            name: "Skyline Tower",
            address: "23-15 44th Dr, Long Island City",
            owner: "United Construction & Development",
            ownerContact: "Chris Jia Shu Xu",
            notes: "Queens' tallest residential tower"
        },
        {
            name: "The Prince / Hilton Garden Inn",
            address: "134-03 35th Ave, Flushing",
            owner: "United Construction & Development",
            ownerContact: "Chris Jia Shu Xu",
            notes: "Hotel property in downtown Flushing"
        },
        {
            name: "Justice Ave Tower",
            address: "88-08 Justice Ave, Elmhurst",
            owner: "United Construction & Development",
            ownerContact: "Chris Jia Shu Xu",
            notes: "Mixed-use development in Elmhurst"
        },
        {
            name: "The Cube",
            address: "24-01 43rd Ave, Long Island City",
            owner: "United Construction & Development",
            ownerContact: "Chris Jia Shu Xu",
            notes: "Modern residential tower in LIC"
        },
        {
            name: "Janet Place Phase One",
            address: "39-08 Janet Pl, Flushing",
            owner: "United Construction & Development",
            ownerContact: "Chris Jia Shu Xu",
            notes: "Co-owned with F&T Group, potential pilot site"
        },

        // Wing Fung Realty Group
        {
            name: "86-33 Whitney Ave Building",
            address: "86-33 Whitney Ave, Elmhurst",
            owner: "Wing Fung Realty Group",
            ownerContact: "Shu Ye",
            notes: "Multifamily building, perfect for Instant Plan PDF"
        },
        {
            name: "86-25/27 Whitney Ave Buildings",
            address: "86-25 Whitney Ave, Elmhurst",
            owner: "Wing Fung Realty Group",
            ownerContact: "Shu Ye",
            notes: "Adjacent multifamily properties"
        },
        {
            name: "88-10 Whitney Ave Building",
            address: "88-10 Whitney Ave, Elmhurst",
            owner: "Wing Fung Realty Group",
            ownerContact: "Shu Ye",
            notes: "Clustered multifamily portfolio on Whitney Ave"
        },
        {
            name: "56th Ave Portfolio",
            address: "90-13/17/19 56th Ave, Elmhurst",
            owner: "Wing Fung Realty Group",
            ownerContact: "Shu Ye",
            notes: "Multiple multifamily buildings"
        },
        {
            name: "Dyker Heights Property",
            address: "6208 8th Ave, Brooklyn",
            owner: "Wing Fung Realty Group",
            ownerContact: "Shu Ye",
            notes: "Brooklyn multifamily property"
        },

        // SCG America
        {
            name: "SCG Plaza II",
            address: "133-36 37th Ave, Flushing",
            owner: "SCG America",
            ownerContact: "Shanghai Construction U.S.",
            notes: "Co-developed with F&T Group"
        },
        {
            name: "Manhattan View @ MiMA",
            address: "460 W 42nd St, Manhattan",
            owner: "SCG America",
            ownerContact: "Shanghai Construction U.S.",
            notes: "Large Manhattan residential complex"
        },

        // XIN Development Group
        {
            name: "Oosten Condominiums",
            address: "429 Kent Ave, Williamsburg",
            owner: "XIN Development Group",
            ownerContact: "Xinyuan Real Estate",
            notes: "Williamsburg waterfront condos"
        },
        {
            name: "Hudson Garden",
            address: "615 10th Ave, Manhattan",
            owner: "XIN Development Group",
            ownerContact: "Xinyuan Real Estate",
            notes: "Hell's Kitchen residential development"
        },
        {
            name: "Bloom 45",
            address: "500 W 45th St, Manhattan",
            owner: "XIN Development Group",
            ownerContact: "Xinyuan Real Estate",
            notes: "Midtown residential tower"
        }
    ];

    // Enhanced building matching with fuzzy search
    const findMatchingBuilding = (property: TargetProperty): Building | undefined => {
        const address = property.address.toLowerCase();
        const name = property.name.toLowerCase();
        
        return buildings.find(building => {
            const buildingAddress = building.address.toLowerCase();
            const buildingName = (building.propertyName || '').toLowerCase();
            
            // Extract street number and name for better matching
            const extractStreetInfo = (addr: string) => {
                const match = addr.match(/(\d+[-\d]*)\s+(.+)/);
                if (match) {
                    return { number: match[1], street: match[2] };
                }
                return { number: '', street: addr };
            };
            
            const targetStreet = extractStreetInfo(address);
            const buildingStreet = extractStreetInfo(buildingAddress);
            
            // Check for exact address matches
            if (buildingAddress.includes(targetStreet.number) && 
                buildingAddress.includes(targetStreet.street.split(',')[0])) {
                return true;
            }
            
            // Check for name matches
            if (buildingName && name && buildingName.includes(name)) {
                return true;
            }
            
            // Check for partial street matches in same borough
            const borough = address.includes('manhattan') ? 'Manhattan' : 
                          address.includes('brooklyn') ? 'Brooklyn' :
                          address.includes('flushing') || address.includes('elmhurst') || address.includes('long island city') ? 'Queens' : '';
            
            if (borough && building.borough === borough) {
                const streetName = targetStreet.street.split(',')[0].trim();
                if (buildingAddress.includes(streetName)) {
                    return true;
                }
            }
            
            return false;
        });
    };

    // Calculate LL97 analysis for each property
    const propertiesWithAnalysis = useMemo(() => {
        return targetProperties.map(property => {
            const building = findMatchingBuilding(property);
            let ll97Analysis = undefined;
            
            if (building) {
                const ll97LimitPerSqFt = LL97Service.getEmissionLimit(building.buildingType, 2025);
                const currentEmissions = building.totalGHGEmissions;
                const allowedEmissions = ll97LimitPerSqFt * building.grossFloorArea;
                const gapAmount = Math.max(0, currentEmissions - allowedEmissions);
                const annualFine = gapAmount > 0 ? gapAmount * 268 : 0;
                
                ll97Analysis = {
                    isCompliant: gapAmount === 0,
                    annualFine,
                    emissionsGap: gapAmount,
                    potentialSavings: annualFine * 10 // 10-year savings potential
                };
            }
            
            return {
                ...property,
                building,
                ll97Analysis
            };
        });
    }, [buildings]);

    // Filter properties
    const filteredProperties = useMemo(() => {
        let filtered = propertiesWithAnalysis;
        
        if (selectedOwner !== 'all') {
            filtered = filtered.filter(p => p.owner === selectedOwner);
        }
        
        if (showOnlyNonCompliant) {
            filtered = filtered.filter(p => p.ll97Analysis && !p.ll97Analysis.isCompliant);
        }
        
        return filtered;
    }, [propertiesWithAnalysis, selectedOwner, showOnlyNonCompliant]);

    // Get unique owners
    const owners = Array.from(new Set(targetProperties.map(p => p.owner)));

    // Calculate summary stats
    const summaryStats = useMemo(() => {
        const propertiesWithData = propertiesWithAnalysis.filter(p => p.building);
        const nonCompliantProperties = propertiesWithData.filter(p => p.ll97Analysis && !p.ll97Analysis.isCompliant);
        const totalFines = nonCompliantProperties.reduce((sum, p) => sum + (p.ll97Analysis?.annualFine || 0), 0);
        const totalSavingsPotential = nonCompliantProperties.reduce((sum, p) => sum + (p.ll97Analysis?.potentialSavings || 0), 0);
        
        return {
            totalProperties: targetProperties.length,
            propertiesWithData: propertiesWithData.length,
            nonCompliantProperties: nonCompliantProperties.length,
            totalFines,
            totalSavingsPotential
        };
    }, [propertiesWithAnalysis]);

    const formatCurrency = (amount: number) => {
        if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(1)}M`;
        } else if (amount >= 1000) {
            return `$${(amount / 1000).toFixed(0)}K`;
        }
        return `$${Math.round(amount).toLocaleString()}`;
    };

    return (
        <div className="min-h-screen bg-neutral-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-neutral-900 mb-2">Target Properties Analysis</h1>
                    <p className="text-xl text-neutral-600 mb-4">LL97 compliance analysis for key developer portfolios in Queens, Manhattan & Brooklyn</p>
                    
                    {/* Usage Instructions */}
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                            <span className="mr-2">🎯</span> How to Use Target Properties Analysis
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">For Investors & Analysts:</h4>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
                                    <li>Filter by owner/developer groups</li>
                                    <li>Review compliance status & fine risks</li>
                                    <li>Identify high-ROI opportunities</li>
                                    <li>Export contact lists for outreach</li>
                                    <li>Analyze portfolio-level strategies</li>
                                </ol>
                            </div>
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">Key Metrics:</h4>
                                <ul className="space-y-1 text-sm text-blue-700">
                                    <li>• <strong>Annual Fines:</strong> Potential LL97 penalties</li>
                                    <li>• <strong>Investment Size:</strong> Estimated retrofit costs</li>
                                    <li>• <strong>ROI Potential:</strong> Return on investment</li>
                                    <li>• <strong>Contact Info:</strong> Direct owner connections</li>
                                </ul>
                            </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-3">
                            💡 <strong>Strategy:</strong> Focus on developers with multiple properties for portfolio-level deals and higher commission potential.
                        </p>
                    </div>
                    
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                        <p className="text-primary-800 text-sm">
                            <strong>💡 Revenue Strategy:</strong> Identify high-value retrofit opportunities, connect property owners with contractors & lenders, and generate revenue through referral commissions and consulting services.
                        </p>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                    <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Portfolio Summary</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-primary-50 p-6 rounded-lg">
                            <div className="text-3xl font-bold text-primary-600">{summaryStats.totalProperties}</div>
                            <div className="text-sm text-neutral-600">Target Properties</div>
                        </div>
                        <div className="bg-success-50 p-6 rounded-lg">
                            <div className="text-3xl font-bold text-success-600">{summaryStats.propertiesWithData}</div>
                            <div className="text-sm text-neutral-600">Properties with Data</div>
                        </div>
                        <div className="bg-danger-50 p-6 rounded-lg">
                            <div className="text-3xl font-bold text-danger-600">{formatCurrency(summaryStats.totalFines)}</div>
                            <div className="text-sm text-neutral-600">Annual LL97 Fines</div>
                        </div>
                        <div className="bg-accent-50 p-6 rounded-lg">
                            <div className="text-3xl font-bold text-accent-600">{formatCurrency(summaryStats.totalSavingsPotential)}</div>
                            <div className="text-sm text-neutral-600">10-Year Savings Potential</div>
                        </div>
                    </div>
                    
                    {/* Revenue Opportunity Section */}
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Contractor Connections */}
                        <div className="bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-primary-500 rounded-lg p-2 mr-3">
                                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0v-3.28a1 1 0 01.684-.948l1.684-.561M19 21v-3.28a1 1 0 00-.684-.948L16.632 16M7.332 3.5a1 1 0 011.336 0L12 6l3.332-2.5a1 1 0 011.336 0L19 6" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-primary-900">Contractor Network</h3>
                            </div>
                            <p className="text-primary-800 text-sm mb-3">Connect property owners with pre-vetted LL97 retrofit specialists</p>
                            <div className="text-2xl font-bold text-primary-900">{formatCurrency(summaryStats.totalSavingsPotential * 0.4)}</div>
                            <div className="text-sm text-primary-700">Total project value potential</div>
                        </div>

                        {/* Financing Connections */}
                        <div className="bg-gradient-to-br from-success-50 to-success-100 border border-success-200 rounded-lg p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-success-500 rounded-lg p-2 mr-3">
                                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-success-900">CPACE Financing</h3>
                            </div>
                            <p className="text-success-800 text-sm mb-3">Connect to low-cost financing for energy efficiency projects</p>
                            <div className="text-2xl font-bold text-success-900">{formatCurrency(summaryStats.totalSavingsPotential * 0.6)}</div>
                            <div className="text-sm text-success-700">Estimated lending volume</div>
                        </div>

                        {/* Incentives & Rebates */}
                        <div className="bg-gradient-to-br from-accent-50 to-accent-100 border border-accent-200 rounded-lg p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-accent-500 rounded-lg p-2 mr-3">
                                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-accent-900">Incentive Programs</h3>
                            </div>
                            <p className="text-accent-800 text-sm mb-3">Maximize rebates from NYSERDA, Con Ed, and city programs</p>
                            <div className="text-2xl font-bold text-accent-900">{formatCurrency(summaryStats.totalSavingsPotential * 0.25)}</div>
                            <div className="text-sm text-accent-700">Potential incentive value</div>
                        </div>
                    </div>

                    {/* Platform Revenue Summary */}
                    <div className="mt-6 bg-gradient-to-r from-warning-50 to-warning-100 border border-warning-200 rounded-lg p-6">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-warning-900 mb-2">🎯 Platform Revenue Opportunity</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium text-warning-800">Contractor Referrals:</span>
                                        <span className="ml-2 text-warning-700">{formatCurrency(summaryStats.totalSavingsPotential * 0.03)} annual commission potential</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-warning-800">Lender Connections:</span>
                                        <span className="ml-2 text-warning-700">{formatCurrency(summaryStats.totalSavingsPotential * 0.01)} lending referral fees</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-warning-800">Premium Services:</span>
                                        <span className="ml-2 text-warning-700">{formatCurrency(summaryStats.totalSavingsPotential * 0.005)} consulting revenue</span>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-warning-200">
                                    <span className="text-base font-bold text-warning-900">
                                        Total Annual Revenue Potential: {formatCurrency(summaryStats.totalSavingsPotential * 0.045)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                    <div className="flex flex-wrap gap-4 items-center">
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2">Filter by Owner</label>
                            <select
                                value={selectedOwner}
                                onChange={(e) => setSelectedOwner(e.target.value)}
                                className="px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="all">All Owners</option>
                                {owners.map(owner => (
                                    <option key={owner} value={owner}>{owner}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="nonCompliant"
                                checked={showOnlyNonCompliant}
                                onChange={(e) => setShowOnlyNonCompliant(e.target.checked)}
                                className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                            />
                            <label htmlFor="nonCompliant" className="text-sm font-medium text-neutral-700">
                                Show only non-compliant properties
                            </label>
                        </div>
                    </div>
                </div>

                {/* Properties Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {filteredProperties.map((property, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-xl font-semibold text-neutral-900">{property.name}</h3>
                                        <p className="text-neutral-600">{property.address}</p>
                                    </div>
                                    {property.ll97Analysis && (
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            property.ll97Analysis.isCompliant 
                                                ? 'bg-success-100 text-success-800' 
                                                : 'bg-danger-100 text-danger-800'
                                        }`}>
                                            {property.ll97Analysis.isCompliant ? 'Compliant' : 'Non-Compliant'}
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm font-medium text-neutral-700">Owner/Contact</p>
                                        <p className="text-neutral-900">{property.owner}</p>
                                        {property.ownerContact && (
                                            <p className="text-sm text-neutral-600">{property.ownerContact}</p>
                                        )}
                                    </div>

                                    {property.building && (
                                        <div className="bg-neutral-50 p-4 rounded-lg">
                                            <p className="text-sm font-medium text-neutral-700 mb-2">Building Data Found</p>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="text-neutral-600">Size</p>
                                                    <p className="font-medium">{property.building.grossFloorArea.toLocaleString()} sq ft</p>
                                                </div>
                                                <div>
                                                    <p className="text-neutral-600">Type</p>
                                                    <p className="font-medium">{property.building.buildingType}</p>
                                                </div>
                                                <div>
                                                    <p className="text-neutral-600">Current Emissions</p>
                                                    <p className="font-medium">{property.building.totalGHGEmissions.toFixed(0)} tons CO2</p>
                                                </div>
                                                <div>
                                                    <p className="text-neutral-600">Energy Star Score</p>
                                                    <p className="font-medium">{property.building.energyStarScore || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {property.ll97Analysis && !property.ll97Analysis.isCompliant && (
                                        <div className="bg-danger-50 p-4 rounded-lg border border-danger-200">
                                            <p className="text-sm font-medium text-danger-800 mb-2">LL97 Non-Compliance Impact</p>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="text-danger-600">Annual Fine</p>
                                                    <p className="font-bold text-danger-800">{formatCurrency(property.ll97Analysis.annualFine)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-danger-600">10-Year Cost</p>
                                                    <p className="font-bold text-danger-800">{formatCurrency(property.ll97Analysis.potentialSavings)}</p>
                                                </div>
                                            </div>
                                            <div className="mt-3 p-3 bg-white rounded border">
                                                <p className="text-xs font-medium text-success-700 mb-1">💰 Revenue Opportunity</p>
                                                <p className="text-xs text-neutral-600">
                                                    Retrofit project value: {formatCurrency(property.ll97Analysis.potentialSavings * 0.4)} | 
                                                    Potential referral commission: {formatCurrency(property.ll97Analysis.potentialSavings * 0.02)}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {!property.building && (
                                        <div className="bg-warning-50 p-4 rounded-lg border border-warning-200">
                                            <p className="text-sm font-medium text-warning-800">No Building Data Found</p>
                                            <p className="text-xs text-warning-700 mt-1">
                                                This property may not be in the NYC disclosure database, or address matching failed.
                                                Consider manual data entry for high-value prospects.
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <p className="text-sm font-medium text-neutral-700 mb-1">Strategic Notes</p>
                                        <p className="text-sm text-neutral-600">{property.notes}</p>
                                    </div>

                                    {/* Action Buttons for Revenue Generation */}
                                    {property.ll97Analysis && !property.ll97Analysis.isCompliant && (
                                        <div className="pt-4 border-t border-neutral-200">
                                            <p className="text-sm font-medium text-neutral-700 mb-3">🎯 Connect & Generate Revenue</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                                <Link 
                                                    to="/connection-hub?tab=contractors"
                                                    className="bg-primary-600 hover:bg-primary-700 text-white text-xs px-3 py-2 rounded-md transition-colors text-center"
                                                >
                                                    🔧 Find Contractors
                                                </Link>
                                                <Link 
                                                    to="/connection-hub?tab=financing"
                                                    className="bg-success-600 hover:bg-success-700 text-white text-xs px-3 py-2 rounded-md transition-colors text-center"
                                                >
                                                    💰 CPACE Financing
                                                </Link>
                                                <Link 
                                                    to="/connection-hub?tab=incentives"
                                                    className="bg-accent-600 hover:bg-accent-700 text-white text-xs px-3 py-2 rounded-md transition-colors text-center"
                                                >
                                                    🎁 Find Incentives
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {property.ll97Analysis && !property.ll97Analysis.isCompliant && (
                                    <div className="mt-6 pt-4 border-t border-neutral-200">
                                        <h4 className="text-sm font-medium text-neutral-900 mb-3">Recommended Next Steps</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center">
                                                <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                                                <span>Schedule energy audit with certified contractor</span>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="w-2 h-2 bg-success-500 rounded-full mr-3"></span>
                                                <span>Explore CPACE financing options</span>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="w-2 h-2 bg-accent-500 rounded-full mr-3"></span>
                                                <span>Apply for available incentives and rebates</span>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="w-2 h-2 bg-warning-500 rounded-full mr-3"></span>
                                                <span>Download detailed retrofit analysis PDF</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {filteredProperties.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-xl text-neutral-500">No properties match the current filters.</p>
                        <button
                            onClick={() => {
                                setSelectedOwner('all');
                                setShowOnlyNonCompliant(false);
                            }}
                            className="mt-4 text-primary-600 hover:text-primary-800"
                        >
                            Clear filters
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TargetPropertiesAnalysis;
